import React, { useEffect, useMemo, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Form, useSearchParams, useSubmit } from 'react-router-dom'

import Badge from '@components/shared/Badge'
import Button from '@components/shared/Button'
import NFIcon from '@components/shared/NFIcon'
import Checkbox from '@components/shared/form/Checkbox'
import { useOutsideClick } from '@hooks/index'

enum Filters {
  ORIGINAL = 'originals',
  EDITABLE = 'editable_copy',
  SOUNDCHECK = 'sc',
  SELLING = 'selling',
  PUBLISHED = 'published',
  SHARED = 'shared',
  PRIVATE = 'private',
}

const VALID_FILTERS = Object.values(Filters)

const FILTER_GROUPS = [
  {
    groupName: 'filter.scores.group.type',
    items: [
      { label: 'filter.scores.originals', value: Filters.ORIGINAL },
      { label: 'filter.scores.editable_copy', value: Filters.EDITABLE },
      { label: 'filter.scores.sc', value: Filters.SOUNDCHECK },
    ],
  },
  {
    groupName: 'filter.scores.group.status',
    items: [
      { label: 'filter.scores.selling', value: Filters.SELLING },
      { label: 'filter.scores.published', value: Filters.PUBLISHED },
      { label: 'filter.scores.shared', value: Filters.SHARED },
      { label: 'filter.scores.private', value: Filters.PRIVATE },
    ],
  },
]

interface Props {
  activeFilters?: string[] | null
}

const FilterDropdown: React.FC<Props> = ({ activeFilters }) => {
  const { t } = useTranslation()
  const submit = useSubmit()
  const [searchParams] = useSearchParams()

  const [selectedFilters, setSelectedFilters] = useState<Set<string>>(
    new Set(keepOnlyValidFilters(activeFilters))
  )
  const [showOptions, setShowOptions] = useState(false)

  const onChecked = (filter: string, val: boolean): void => {
    const newFilters = new Set(selectedFilters)
    if (val) {
      newFilters.add(filter)
    } else {
      newFilters.delete(filter)
    }
    setSelectedFilters(newFilters)
  }

  const applyFilters = (): void => {
    const target: any = {
      ...Object.fromEntries(searchParams.entries()),
      filters: [...selectedFilters].join(','),
    }
    if (target.filters.length === 0) delete target.filters

    submit(target, { replace: !!activeFilters })
    setShowOptions(false)
  }

  const clearFilters = (): void => {
    searchParams.delete('filters')
    submit({ ...Object.fromEntries(searchParams.entries()) }, { replace: !!activeFilters })
    setSelectedFilters(new Set())
    setShowOptions(false)
  }

  // If active filters change, update selection
  useEffect(() => {
    setSelectedFilters(new Set(keepOnlyValidFilters(activeFilters)))
  }, [activeFilters])

  const ref = useOutsideClick<HTMLDivElement>(() => {
    setSelectedFilters(new Set(keepOnlyValidFilters(activeFilters)))
    setShowOptions(false)
  })

  const disabled = useMemo(() => {
    if (!activeFilters) {
      return selectedFilters.size === 0
    }
    if (activeFilters.length !== selectedFilters.size) return false
    return activeFilters.every((filter) => selectedFilters.has(filter))
  }, [activeFilters, selectedFilters])

  return (
    <Form role="search" className="relative md:mr-6 text-base">
      <button
        type="button"
        onClick={() => {
          setShowOptions(!showOptions)
        }}
        className="flex flex-row items-center rounded-sm py-1 mix-blend-multiply pl-2.5 pr-1.5 hover:bg-gray-highlight focus:bg-gray-highlight dark:mix-blend-normal dark:hover:bg-gray-highlight-dm dark:focus:bg-gray-highlight-dm">
        <NFIcon icon="filter" className="text-xl" />
        <span className="hidden md:inline-flex ml-2.5">
          {selectedFilters.size === 0
            ? t('filter.scores.all')
            : [...selectedFilters].map((filter) => t(`filter.scores.${filter}`)).join(', ')}
        </span>
        {!!selectedFilters.size && <Badge className="ml-1 md:hidden">{selectedFilters.size}</Badge>}
        <NFIcon icon="chevron-down" className="ml-1 md:ml-2.5 text-[18px]" />
      </button>

      <div
        className={classNames(
          'fixed inset-0 z-20 block bg-black bg-opacity-20 md:hidden',
          showOptions ? '' : 'hidden'
        )}
      />
      <div
        className={classNames(
          'fixed right-0 bottom-0 transition-[bottom] left-0 z-30 flex flex-col h-[315px] md:h-auto rounded-md md:rounded rounded-b-none bg-white p-4 pt-5 shadow-menu dark:bg-black-bg2 md:p-2.5 md:absolute md:top-0 md:bottom-auto md:left-auto md:rounded-b',
          showOptions ? '' : 'bottom-[-315px] md:hidden'
        )}
        ref={ref}>
        {FILTER_GROUPS.map((group, index) => (
          <React.Fragment key={`group-${group.groupName}`}>
            {index !== 0 && (
              <hr className="hidden text-gray-border my-1.5 dark:text-gray-border-dm md:block" />
            )}
            <h1
              className={classNames('text-lg font-medium mb-1 md:hidden', {
                'mt-3.5': index !== 0,
              })}>
              {t(group.groupName)}
            </h1>
            <div className="flex flex-row flex-wrap items-center md:flex-col">
              {group.items.map((item) => (
                <Checkbox
                  key={item.value}
                  value={item.value}
                  label={t(item.label)}
                  checked={selectedFilters.has(item.value)}
                  onChecked={(val) => onChecked(item.value, val)}
                  name="filters"
                  className="w-1/2 whitespace-nowrap text-md my-1.5 md:first:mt-0 md:w-full md:text-base"
                />
              ))}
            </div>
          </React.Fragment>
        ))}
        <hr className="hidden text-gray-border my-1.5 dark:text-gray-border-dm md:block" />
        <div className="flex flex-col w-full flex-1 justify-end">
          <Button
            variant="primary"
            className="py-2 text-md mt-3.5 md:mt-1.5 md:py-1.5 md:text-base"
            onClick={applyFilters}
            disabled={disabled}>
            Apply filters
          </Button>
          <button
            type="button"
            className="py-2 orange-link mt-1.5 text-md md:py-0 md:text-base"
            onClick={clearFilters}
            disabled={!activeFilters?.length}>
            Clear filters
          </button>
        </div>
      </div>
    </Form>
  )
}

export default FilterDropdown

function keepOnlyValidFilters(filters?: string[] | null): string[] {
  return (filters ?? []).filter((f) => VALID_FILTERS.includes(f as Filters))
}
